<template>
  <div class="monitor-video-card" :class="{ compact }">
    <div class="corner corner-lt"></div>
    <div class="corner corner-rb"></div>
    <div class="video-inner">
      <div class="player-shell">
        <vue3VideoPlay
          v-if="isPlayerActive"
          ref="playerRef"
          class="monitor-player"
          width="100%"
          height="100%"
          title=""
          :src="src"
          :type="type"
          :autoPlay="playerAutoPlay"
          :muted="true"
          :control="false"
          :controlBtns="false"
          :loop="true"
          preload="none"
          @play="onPlayerPlay"
          @pause="onPlayerPause"
        />
        <button
          v-if="isPlayerActive && clickToToggle"
          class="video-toggle-layer"
          type="button"
          :aria-label="isPlaying ? '暂停视频' : '播放视频'"
          @click.stop="togglePlayer"
        >
          <span v-if="!isPlaying" class="resume-mark"></span>
        </button>
        <button
          v-else-if="lazy"
          class="video-placeholder"
          type="button"
          :style="placeholderStyle"
          @click.stop="activatePlayer"
        >
          <span class="play-mark"></span>
          <strong>{{ title }}</strong>
          <em>点击加载视频</em>
        </button>
      </div>
      <div class="video-mask"></div>
      <div class="video-footer">
        <span v-for="item in cameras" :key="item">{{ item }}</span>
        <span class="time">{{ timeText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  src: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: "video/mp4",
  },
  cameras: {
    type: Array,
    default: () => ["CAM-01 在线"],
  },
  compact: {
    type: Boolean,
    default: false,
  },
  lazy: {
    type: Boolean,
    default: false,
  },
  poster: {
    type: String,
    default: "",
  },
  clickToToggle: {
    type: Boolean,
    default: false,
  },
  autoPlayOnLoad: {
    type: Boolean,
    default: false,
  },
});

const playerRef = ref(null);
const isPlayerActive = ref(!props.lazy);
const isPlaying = ref(false);
const now = ref(new Date());
let timer = null;

const playerAutoPlay = computed(() => props.lazy && props.autoPlayOnLoad);
const placeholderStyle = computed(() => (
  props.poster
    ? { backgroundImage: `linear-gradient(180deg, rgba(1, 28, 28, 0.26), rgba(1, 20, 23, 0.82)), url(${props.poster})` }
    : {}
));

function activatePlayer() {
  isPlayerActive.value = true;
  isPlaying.value = props.autoPlayOnLoad;
}

function togglePlayer() {
  if (!playerRef.value) return;
  if (isPlaying.value) {
    playerRef.value.pause && playerRef.value.pause();
    isPlaying.value = false;
  } else {
    playerRef.value.play && playerRef.value.play();
    isPlaying.value = true;
  }
}

function onPlayerPlay() {
  isPlaying.value = true;
}

function onPlayerPause() {
  isPlaying.value = false;
}

const pad = (value) => String(value).padStart(2, "0");
const timeText = computed(() => {
  const date = now.value;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
});

onMounted(() => {
  timer = setInterval(() => {
    now.value = new Date();
  }, 30000);
});

onBeforeUnmount(() => {
  clearInterval(timer);
});
</script>

<style lang="less" scoped>
.monitor-video-card {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 28px;
  box-sizing: border-box;
  border: 2px solid rgba(143, 255, 241, 0.72);
  background:
    linear-gradient(180deg, rgba(7, 62, 58, 0.76), rgba(3, 24, 25, 0.92)),
    radial-gradient(circle at 50% 0, rgba(71, 255, 228, 0.22), transparent 46%);
  box-shadow:
    inset 0 0 32px rgba(32, 255, 230, 0.36),
    0 0 24px rgba(23, 239, 217, 0.28);
  clip-path: polygon(18px 0, calc(100% - 18px) 0, 100% 18px, 100% calc(100% - 18px), calc(100% - 18px) 100%, 18px 100%, 0 calc(100% - 18px), 0 18px);
}

.monitor-video-card::before,
.monitor-video-card::after {
  content: "";
  position: absolute;
  left: 34px;
  right: 34px;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(225, 255, 252, 0.92), transparent);
  z-index: 2;
}

.monitor-video-card::before {
  top: 8px;
}

.monitor-video-card::after {
  bottom: 8px;
}

.corner {
  position: absolute;
  width: 102px;
  height: 44px;
  z-index: 3;
  pointer-events: none;
}

.corner::before,
.corner::after {
  content: "";
  position: absolute;
  background: rgba(235, 255, 252, 0.95);
  box-shadow: 0 0 10px rgba(97, 255, 238, 0.8);
}

.corner::before {
  width: 100%;
  height: 3px;
}

.corner::after {
  width: 3px;
  height: 100%;
}

.corner-lt {
  left: 2px;
  top: 2px;
}

.corner-lt::before,
.corner-lt::after {
  left: 0;
  top: 0;
}

.corner-rb {
  right: 2px;
  bottom: 2px;
  transform: rotate(180deg);
}

.corner-rb::before,
.corner-rb::after {
  left: 0;
  top: 0;
}

.video-inner {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border: 2px solid rgba(82, 241, 224, 0.66);
  background: #031919;
  box-shadow: inset 0 0 24px rgba(20, 238, 218, 0.32);
}

.player-shell {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.monitor-player {
  display: block;
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
}

.video-placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #f4fffd;
  cursor: pointer;
  background:
    radial-gradient(circle at 50% 44%, rgba(30, 255, 229, 0.16), transparent 32%),
    linear-gradient(180deg, rgba(2, 54, 50, 0.88), rgba(1, 20, 23, 0.96));
  background-position: center;
  background-size: cover;
}

.video-placeholder::before {
  content: "";
  position: absolute;
  inset: 18px;
  border: 1px solid rgba(92, 255, 239, 0.22);
  box-shadow: inset 0 0 26px rgba(36, 255, 232, 0.1);
}

.play-mark {
  position: relative;
  z-index: 1;
  width: 82px;
  height: 82px;
  border-radius: 50%;
  border: 2px solid rgba(82, 255, 240, 0.76);
  background: radial-gradient(circle, rgba(0, 132, 119, 0.82), rgba(0, 56, 54, 0.88));
  box-shadow: 0 0 22px rgba(56, 255, 235, 0.34);
}

.play-mark::after {
  content: "";
  position: absolute;
  left: 32px;
  top: 24px;
  width: 0;
  height: 0;
  border-top: 17px solid transparent;
  border-bottom: 17px solid transparent;
  border-left: 24px solid #ffffff;
  filter: drop-shadow(0 0 8px rgba(80, 255, 239, 0.75));
}

.video-placeholder strong,
.video-placeholder em {
  position: relative;
  z-index: 1;
  font-style: normal;
  text-shadow: 0 0 10px rgba(92, 255, 239, 0.58);
}

.video-placeholder strong {
  font-size: 30px;
  font-weight: 700;
  line-height: 38px;
}

.video-placeholder em {
  color: #6efff3;
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
}

.video-toggle-layer {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: transparent;
}

.resume-mark {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 82px;
  height: 82px;
  margin: -41px 0 0 -41px;
  border-radius: 50%;
  border: 2px solid rgba(82, 255, 240, 0.76);
  background: radial-gradient(circle, rgba(0, 132, 119, 0.82), rgba(0, 56, 54, 0.88));
  box-shadow: 0 0 22px rgba(56, 255, 235, 0.34);
}

.resume-mark::after {
  content: "";
  position: absolute;
  left: 32px;
  top: 24px;
  width: 0;
  height: 0;
  border-top: 17px solid transparent;
  border-bottom: 17px solid transparent;
  border-left: 24px solid #ffffff;
  filter: drop-shadow(0 0 8px rgba(80, 255, 239, 0.75));
}

.video-mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(4, 35, 32, 0.16), transparent 34%, rgba(0, 18, 18, 0.14)),
    repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.04) 0, rgba(255, 255, 255, 0.04) 1px, transparent 1px, transparent 5px);
}

.video-title {
  position: absolute;
  left: 22px;
  top: 16px;
  z-index: 3;
  min-width: 178px;
  height: 80px;
  padding: 0 22px;
  box-sizing: border-box;
  line-height: 80px;
  color: #f7fffd;
  font-size: 26px;
  font-weight: 700;
  text-align: left;
  background: linear-gradient(90deg, rgba(7, 62, 58, 0.95), rgba(7, 62, 58, 0.62), transparent);
  text-shadow: 0 0 8px rgba(125, 255, 238, 0.65);
}

.video-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 34px;
  padding: 0 24px;
  box-sizing: border-box;
  color: #f4fffb;
  background: rgba(0, 45, 40, 0.72);
  text-shadow: 0 0 6px rgba(77, 255, 230, 0.65);
  height: 60px;
  font-size: 26px;
}

.video-footer .time {
  margin-left: auto;
}

.compact {
  padding: 12px;
}

.compact .video-title {
  min-width: 86px;
  height: 80px;
  line-height: 80px;
  font-size: 26px;
  padding: 0 14px;
  left: 0;
  top: 0;
}

.compact .video-footer {
  height: 60px;
  font-size: 26px;
  gap: 12px;
  padding: 0 20px;
}

.player-shell :deep(.d-player-wrap),
.player-shell :deep(.d-player),
.player-shell :deep(.d-player-box),
.player-shell :deep(.d-player-video),
.player-shell :deep(.d-player-video-wrap) {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
  background: transparent !important;
}

.player-shell :deep(video) {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  object-fit: cover !important;
}

:deep(.d-player-control),
:deep(.d-player-state),
:deep(.d-play-btn),
:deep(.d-player-title),
:deep(.d-player-loading) {
  display: none !important;
}
</style>
